import { useEffect, useState, type JSX } from "react";
import { PrismToast } from "@prism-ui/react";
import { subscribe, removeToast, ToastItem, ToastType } from "./toast";
import "./Toaster.css";

export const DEFAULT_AUTO_CLOSE = 8000;
// prism-toast's exit animation is 280ms; drop the element only after it finishes.
export const EXIT_ANIMATION_MS = 400;

const TONES: Record<ToastType, "success" | "danger" | "info" | "caution"> = {
  success: "success",
  error: "danger",
  info: "info",
  warn: "caution",
};

const ICONS: Record<ToastType, string> = {
  success: "\u2713", // ✓
  error: "\u2715", // ✕
  info: "i",
  warn: "!",
};

function ToastRow({ item }: { item: ToastItem }): JSX.Element {
  const [open, setOpen] = useState(true);
  // prism-toast either auto-dismisses or shows a close button, never both.
  const sticky = item.autoClose === false || (item.autoClose !== undefined && item.autoClose <= 0);
  const duration = typeof item.autoClose === "number" ? item.autoClose : DEFAULT_AUTO_CLOSE;

  useEffect(() => {
    if (open) return;
    const timer = window.setTimeout(() => removeToast(item.id), EXIT_ANIMATION_MS);
    return () => window.clearTimeout(timer);
  }, [open, item.id]);

  const close = (): void => setOpen(false);

  return (
    <PrismToast
      className={`clr-toast clr-toast--${item.type}`}
      data-toast-id={item.id}
      position="top-right"
      tone={TONES[item.type]}
      open={open}
      duration={duration}
      dismissible={sticky}
      dismissLabel="Close notification"
      onPrismAutoDismiss={close}
      onPrismDismiss={close}
    >
      <span slot="asset-leading" className="clr-toast__icon" aria-hidden="true">
        {ICONS[item.type]}
      </span>
      {item.message}
    </PrismToast>
  );
}

/**
 * App-wide toast container. Mount exactly once (in App.jsx). Each toast is a
 * `prism-toast`; Prism positions them fixed and stacks concurrent toasts.
 */
export function Toaster(): JSX.Element {
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => subscribe(setItems), []);

  return (
    <>
      {items.map((item) => (
        <ToastRow key={item.id} item={item} />
      ))}
    </>
  );
}
